import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Debug helper: last 8 chars of token (all JWTs share the same prefix, so suffix is what differs)
const tokenTail = (t: string | undefined | null) => (t ? t.slice(-8) : 'none');

// Module-level diagnostic
console.log('[TabSync] multiAccountStore module loaded');

/**
 * Stored account information including refresh token and user details.
 * One entry per email address (not per email+org combination).
 */
export interface StoredAccount {
  email: string;
  firstName: string;
  lastName: string;
  refreshToken: string;
  addedAt: string; // ISO timestamp
  lastUsedAt: string; // ISO timestamp
  currentOrganisationName?: string; // Current organisation name (cached from last login/switch)
  currentOrganisationId?: string; // Current organisation ID (from identity token)
}

interface MultiAccountState {
  // All stored accounts
  accounts: StoredAccount[];

  // Email of the currently active account (null if no account selected)
  currentAccountEmail: string | null;

  // Monotonic timestamp updated on every state change, used for cross-tab sync ordering
  lastUpdatedAt: number;

  // Actions
  addAccount: (account: Omit<StoredAccount, 'addedAt' | 'lastUsedAt'>) => void;
  updateAccount: (
    email: string,
    updates: Partial<Omit<StoredAccount, 'email'>>
  ) => void;
  removeAccount: (email: string) => void;
  setCurrentAccount: (email: string | null) => void;
  getAccount: (email: string) => StoredAccount | undefined;
  getAllAccounts: () => StoredAccount[];
  getExpiredAccounts: (
    checkExpiration: (refreshToken: string) => boolean
  ) => StoredAccount[];
  getCurrentAccount: () => StoredAccount | undefined;
  clearAllAccounts: () => void;
}

const STORAGE_KEY = 'multi-accounts-storage';

/**
 * Multi-account store for managing multiple logged-in accounts.
 * Stores refresh tokens and user information in localStorage.
 * Identity tokens are stored separately in sessionStorage (per-tab).
 */
export const useMultiAccountStore = create<MultiAccountState>()(
  persist(
    (set, get) => ({
      accounts: [],
      currentAccountEmail: null,
      lastUpdatedAt: Date.now(),

      addAccount: accountData => {
        const now = new Date().toISOString();
        const newAccount: StoredAccount = {
          ...accountData,
          addedAt: now,
          lastUsedAt: now,
        };

        console.log('[TabSync] addAccount called', {
          email: accountData.email,
          tokenTail: tokenTail(accountData.refreshToken),
        });

        set(state => {
          // Check if account with this email already exists
          const existingIndex = state.accounts.findIndex(
            acc => acc.email === accountData.email
          );

          if (existingIndex >= 0) {
            // Update existing account
            const updatedAccounts = [...state.accounts];
            updatedAccounts[existingIndex] = {
              ...newAccount,
              addedAt: state.accounts[existingIndex].addedAt, // Keep original addedAt
            };
            return { accounts: updatedAccounts, lastUpdatedAt: Date.now() };
          } else {
            // Add new account
            return {
              accounts: [...state.accounts, newAccount],
              lastUpdatedAt: Date.now(),
            };
          }
        });

        // Verify localStorage was written by zustand persist
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          const parsed = raw ? JSON.parse(raw) : null;
          const storedToken = (
            parsed?.state?.accounts as StoredAccount[]
          )?.find(
            (a: StoredAccount) => a.email === accountData.email
          )?.refreshToken;
          console.log('[TabSync] addAccount: localStorage verification', {
            storageKeyExists: !!raw,
            storedTail: tokenTail(storedToken),
            inputTail: tokenTail(accountData.refreshToken),
            match: storedToken === accountData.refreshToken,
            lastUpdatedAt: parsed?.state?.lastUpdatedAt,
          });
        } catch (e) {
          console.error('[TabSync] addAccount: localStorage read failed', e);
        }
      },

      updateAccount: (email, updates) => {
        set(state => {
          const accountIndex = state.accounts.findIndex(
            acc => acc.email === email
          );

          if (accountIndex === -1) {
            console.warn(`Account with email ${email} not found`);
            return state;
          }

          const updatedAccounts = [...state.accounts];
          updatedAccounts[accountIndex] = {
            ...updatedAccounts[accountIndex],
            ...updates,
          };

          return { accounts: updatedAccounts, lastUpdatedAt: Date.now() };
        });
      },

      removeAccount: email => {
        set(state => ({
          accounts: state.accounts.filter(acc => acc.email !== email),
          currentAccountEmail:
            state.currentAccountEmail === email
              ? null
              : state.currentAccountEmail,
          lastUpdatedAt: Date.now(),
        }));
      },

      setCurrentAccount: email => {
        if (email !== null) {
          const state = get();
          const accountIndex = state.accounts.findIndex(
            acc => acc.email === email
          );

          if (accountIndex === -1) {
            console.warn(`Cannot set current account: ${email} not found`);
            return;
          }

          // Update both currentAccountEmail and lastUsedAt in a single update
          set(state => {
            const updatedAccounts = [...state.accounts];
            updatedAccounts[accountIndex] = {
              ...updatedAccounts[accountIndex],
              lastUsedAt: new Date().toISOString(),
            };
            return {
              currentAccountEmail: email,
              accounts: updatedAccounts,
              lastUpdatedAt: Date.now(),
            };
          });
        } else {
          set({ currentAccountEmail: email, lastUpdatedAt: Date.now() });
        }
      },

      getAccount: email => {
        return get().accounts.find(acc => acc.email === email);
      },

      getAllAccounts: () => {
        // Return accounts sorted by lastUsedAt (most recent first)
        return [...get().accounts].sort(
          (a, b) =>
            new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime()
        );
      },

      getExpiredAccounts: checkExpiration => {
        return get().accounts.filter(account =>
          checkExpiration(account.refreshToken)
        );
      },

      getCurrentAccount: () => {
        const state = get();
        if (!state.currentAccountEmail) {
          return undefined;
        }
        return state.getAccount(state.currentAccountEmail);
      },

      clearAllAccounts: () => {
        set({
          accounts: [],
          currentAccountEmail: null,
          lastUpdatedAt: Date.now(),
        });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: state => ({
        accounts: state.accounts,
        currentAccountEmail: state.currentAccountEmail,
        lastUpdatedAt: state.lastUpdatedAt,
      }),
      onRehydrateStorage: () => state => {
        console.log('[TabSync] onRehydrateStorage called', {
          hasState: !!state,
          accountCount: state?.accounts?.length ?? 0,
          currentEmail: state?.currentAccountEmail,
        });

        // Migration: Ensure all accounts have firstName and lastName
        if (state && state.accounts) {
          const needsMigration = state.accounts.some(
            acc => !acc.firstName || !acc.lastName
          );

          if (needsMigration) {
            console.log('🔄 Migrating accounts to include firstName/lastName');
            const migratedAccounts = state.accounts.map(acc => {
              // If firstName/lastName are missing, extract from email as fallback
              if (!acc.firstName || !acc.lastName) {
                const emailParts = acc.email.split('@')[0].split('.');
                return {
                  ...acc,
                  firstName: acc.firstName || emailParts[0] || 'User',
                  lastName: acc.lastName || emailParts[1] || '',
                };
              }
              return acc;
            });
            state.accounts = migratedAccounts;
          }
        }
      },
    }
  )
);

let crossTabSyncInitialised = false;

/**
 * Cross-tab storage sync for multi-account store.
 *
 * When another tab updates the multi-account store (e.g., refreshes a token
 * or switches org), this listener syncs the changes into this tab's in-memory
 * state, preventing stale refresh tokens from triggering replay detection.
 *
 * If the organisation changed in another tab, dispatches a custom event
 * for the UI to show a blocking modal.
 */
function initCrossTabSync(): void {
  if (typeof window === 'undefined') {
    console.log('[TabSync] initCrossTabSync: no window, skipping');
    return;
  }
  if (crossTabSyncInitialised) {
    console.log('[TabSync] initCrossTabSync: already initialised, skipping');
    return;
  }
  crossTabSyncInitialised = true;

  console.log('[TabSync] registering storage event listener');

  let eventCounter = 0;

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;

    eventCounter++;
    const eventId = eventCounter;

    try {
      const parsed = JSON.parse(event.newValue);
      const newState = parsed?.state;
      if (!newState?.accounts) return;

      const currentState = useMultiAccountStore.getState();
      const currentEmail = currentState.currentAccountEmail;

      const remoteAccount = (newState.accounts as StoredAccount[]).find(
        (acc: StoredAccount) => acc.email === currentEmail
      );
      const currentAccount = currentEmail
        ? currentState.accounts.find(acc => acc.email === currentEmail)
        : undefined;

      // Log everything in one shot for each event
      console.log(`[TabSync] event #${eventId}`, {
        currentEmail,
        localTail: tokenTail(currentAccount?.refreshToken),
        remoteTail: tokenTail(remoteAccount?.refreshToken),
        tokenChanged:
          currentAccount?.refreshToken !== remoteAccount?.refreshToken,
        localTimestamp: currentState.lastUpdatedAt,
        remoteTimestamp: newState.lastUpdatedAt,
        remoteIsNewer:
          (newState.lastUpdatedAt ?? 0) > currentState.lastUpdatedAt,
      });

      if (!currentEmail) return;

      // Only sync if the remote state is newer than our current state
      const remoteUpdatedAt = (newState.lastUpdatedAt as number) ?? 0;
      if (remoteUpdatedAt <= currentState.lastUpdatedAt) {
        console.log(`[TabSync] event #${eventId}: skipped (not newer)`);
        return;
      }

      if (!currentAccount || !remoteAccount) {
        console.log(`[TabSync] event #${eventId}: skipped (account not found)`);
        return;
      }

      // Check if the organisation changed in another tab
      if (
        currentAccount.currentOrganisationId &&
        remoteAccount.currentOrganisationId &&
        currentAccount.currentOrganisationId !==
          remoteAccount.currentOrganisationId
      ) {
        console.log(`[TabSync] event #${eventId}: org changed!`, {
          from: currentAccount.currentOrganisationId,
          to: remoteAccount.currentOrganisationId,
        });
        window.dispatchEvent(
          new CustomEvent('auth:org-changed-in-other-tab', {
            detail: {
              previousOrgId: currentAccount.currentOrganisationId,
              newOrgId: remoteAccount.currentOrganisationId,
              newOrgName: remoteAccount.currentOrganisationName,
            },
          })
        );
        return;
      }

      // Same org: silently sync refresh token and other account data
      if (currentAccount.refreshToken !== remoteAccount.refreshToken) {
        console.log(`[TabSync] event #${eventId}: SYNCING token`, {
          oldTail: tokenTail(currentAccount.refreshToken),
          newTail: tokenTail(remoteAccount.refreshToken),
        });
        useMultiAccountStore.setState({
          accounts: newState.accounts,
          currentAccountEmail: newState.currentAccountEmail,
          lastUpdatedAt: remoteUpdatedAt,
        });
      } else {
        console.log(`[TabSync] event #${eventId}: token unchanged`);
      }
    } catch (e) {
      console.error(`[TabSync] event #${eventId}: parse error`, e);
    }
  });
}

// Register cross-tab sync at module level — not inside onRehydrateStorage,
// which doesn't re-fire on Vite HMR (the store instance is preserved across updates).
initCrossTabSync();
